/**
 * Tâche 1131 (Eric 2026-06-09) — Renvois EN/FR manquants, en masse.
 *
 * B1130 crée le renvoi anglais au moment où le pipeline découvre un terme :
 * tout le dictionnaire antérieur est resté sans renvoi, donc une recherche en
 * anglais ne trouvait rien. Ce modal liste les renvois manquants et les crée
 * d'un clic.
 *
 * Aperçu obligatoire (décision d'Eric) : l'anglais entre parenthèses attrape
 * aussi des parenthèses françaises (« Droit de Cotisation (Général) ») —
 * mieux vaut décocher avant que nettoyer après.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, SpinnerGap, ArrowRight } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CrossRefBackfillModal({ open, onClose, onGenerated, lang = "fr" }) {
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [totalScanned, setTotalScanned] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(new Set());
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/jargon/cross-references/preview`, {
          withCredentials: true,
        });
        const list = data?.proposals || [];
        setProposals(list);
        setTotalScanned(data?.total_scanned || 0);
        setSelected(new Set(list.map((p) => p.id)));
      } catch (e) {
        toast.error(
          (lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message),
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [open, lang]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === proposals.length) setSelected(new Set());
    else setSelected(new Set(proposals.map((p) => p.id)));
  };

  const doGenerate = async () => {
    if (selected.size === 0) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/api/jargon/cross-references/generate`,
        { ids: [...selected] },
        { withCredentials: true },
      );
      toast.success(
        lang === "fr"
          ? `${data.created_count} renvoi(s) anglais créé(s). Les titres français n'ont pas changé.`
          : `${data.created_count} English cross-reference(s) created.`,
      );
      onGenerated?.();
      onClose?.();
    } catch (e) {
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message),
      );
    } finally {
      setGenerating(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="cross-ref-modal"
    >
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-2xl flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
          <h2 className="text-base font-semibold text-zinc-900">
            {lang === "fr" ? "Renvois EN/FR manquants" : "Missing EN/FR cross-references"}
          </h2>
          <button
            onClick={onClose}
            className="text-zinc-400 transition-colors hover:text-zinc-900"
            data-testid="cross-ref-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 text-xs text-zinc-700">
          {lang === "fr" ? (
            <>
              Chaque renvoi crée une entrée anglaise qui pointe vers la fiche
              française — une recherche en anglais trouvera enfin le terme. Les{" "}
              <strong>titres français ne changent pas</strong>, et l'anglais est
              ajouté en alias sur l'entrée d'origine.
              <br />
              <span className="text-zinc-500">
                {totalScanned} terme(s) analysé(s), {proposals.length} renvoi(s) manquant(s).
              </span>
            </>
          ) : (
            <>
              Each cross-reference adds an English entry pointing to the French one.
              French titles are left untouched.
              <br />
              <span className="text-zinc-500">
                {totalScanned} scanned, {proposals.length} missing.
              </span>
            </>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-16 text-center text-sm text-zinc-500">
              <SpinnerGap size={20} className="inline animate-spin mr-2" />
              {lang === "fr" ? "Analyse du dictionnaire…" : "Analyzing dictionary…"}
            </div>
          ) : proposals.length === 0 ? (
            <div className="py-16 text-center text-sm text-zinc-500" data-testid="cross-ref-empty">
              {lang === "fr"
                ? "Aucun renvoi manquant — le dictionnaire est déjà bilingue."
                : "No missing cross-reference — the dictionary is already bilingual."}
            </div>
          ) : (
            <>
              <div className="sticky top-0 bg-zinc-50 border-b border-zinc-200 px-3 py-2 flex items-center gap-2 text-xs text-zinc-700">
                <input
                  type="checkbox"
                  checked={selected.size > 0 && selected.size === proposals.length}
                  onChange={toggleAll}
                  data-testid="cross-ref-select-all"
                />
                <span className="flex-1">
                  {lang === "fr"
                    ? `${selected.size} / ${proposals.length} sélectionné(s)`
                    : `${selected.size} / ${proposals.length} selected`}
                </span>
              </div>
              <ul className="divide-y divide-zinc-100" data-testid="cross-ref-list">
                {proposals.map((p) => {
                  const isSel = selected.has(p.id);
                  return (
                    <li
                      key={p.id}
                      className={`px-3 py-2 flex items-start gap-2 cursor-pointer transition-colors ${isSel ? "bg-emerald-50" : "hover:bg-zinc-50"}`}
                      onClick={() => toggle(p.id)}
                      data-testid={`cross-ref-row-${p.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggle(p.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1"
                        data-testid={`cross-ref-checkbox-${p.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-900 flex items-center gap-1.5 flex-wrap">
                          <span className="font-semibold">{p.english_term}</span>
                          <ArrowRight size={12} className="text-zinc-400" />
                          <span className="text-zinc-600">{p.source_term}</span>
                        </div>
                        <div className="text-[10px] text-zinc-400 mt-0.5 flex items-center gap-1.5">
                          {p.reason}
                          {p.reason !== "paire français/anglais connue" && (
                            <span
                              className="px-1 py-0.5 rounded bg-amber-50 text-amber-700 font-semibold"
                              data-testid={`cross-ref-verify-${p.id}`}
                            >
                              {lang === "fr" ? "à vérifier" : "verify"}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-zinc-200">
          <button
            onClick={onClose}
            disabled={generating}
            className="text-xs px-3 py-1.5 rounded border border-zinc-300 bg-white transition-colors hover:bg-zinc-50 disabled:opacity-50"
            data-testid="cross-ref-cancel"
          >
            {lang === "fr" ? "Annuler" : "Cancel"}
          </button>
          <button
            onClick={doGenerate}
            disabled={generating || selected.size === 0}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-emerald-700 text-white transition-colors hover:bg-emerald-800 disabled:opacity-50"
            data-testid="cross-ref-confirm"
          >
            {generating
              ? lang === "fr"
                ? "Création…"
                : "Creating…"
              : lang === "fr"
                ? `Créer les ${selected.size} renvois`
                : `Create ${selected.size} cross-reference(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}
